import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { AmbientNetwork } from '../components/AmbientNetwork';
import { CaseMark } from '../components/CaseMark';
import { SourceList } from '../components/SourceList';
import {
  ArrowRightIcon,
  BookmarkIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  InfoIcon,
  LinkIcon,
} from '../components/Icons';
import { cases, caseMap } from '../data/cases';
import { categories } from '../data/types';
import { advanceDiscoveryQueue, buildDiscoveryQueue, hydrateDiscoveryQueue } from '../lib/discovery';
import { navigate } from '../lib/router';
import { useAtlas } from '../lib/store';

type SheetMode = 'actions' | 'brief';

export function HomePage() {
  const {
    discoveryQueue,
    skippedCases,
    lastHomeCase,
    bookmarkedCases,
    viewedCases,
    toggleCaseBookmark,
    markCaseViewed,
    recordSkipped,
    setDiscoveryQueue,
    setLastHomeCase,
    markVisited,
  } = useAtlas();

  const [offset, setOffset] = useState(0);
  const [motion, setMotion] = useState<'next' | 'prev'>('next');
  const [history, setHistory] = useState<string[]>([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<SheetMode>('actions');
  const [copied, setCopied] = useState(false);

  const initialized = useRef(false);
  const timerRef = useRef<number | null>(null);
  const suppressClick = useRef(false);
  const suppressTimer = useRef<number | null>(null);
  const gesture = useRef({ x: 0, y: 0, pointerId: -1, longPress: false, dragging: false });

  const currentId = discoveryQueue[0] ?? '';
  const study = caseMap.get(currentId) ?? cases.find((item) => item.isNew) ?? cases[0];
  const category = categories.find((item) => item.id === study.category);
  const bookmarked = bookmarkedCases.includes(study.id);
  const viewed = viewedCases.includes(study.id);
  const hasPrevious = history.length > 0;
  const hasNext = discoveryQueue.length > 1;

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    let queue = discoveryQueue.length
      ? hydrateDiscoveryQueue(discoveryQueue, skippedCases)
      : buildDiscoveryQueue({ lastHomeCase, skippedCases });
    if (queue.length === 0) queue = cases.map((item) => item.id);
    if (queue.length > 1 && queue[0] === lastHomeCase) {
      queue = [...queue.slice(1), queue[0]];
    }
    setDiscoveryQueue(queue);
    setLastHomeCase(queue[0] ?? '');
    // The queue is initialized once per home visit. Later advances are explicit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (sheetMode === 'brief') setSheetMode('actions');
        else setSheetOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [sheetOpen, sheetMode]);

  useEffect(() => {
    if (copied) {
      const timeout = window.setTimeout(() => setCopied(false), 1800);
      return () => window.clearTimeout(timeout);
    }
  }, [copied]);

  const clearLongPress = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const suppressForAWhile = () => {
    suppressClick.current = true;
    if (suppressTimer.current !== null) window.clearTimeout(suppressTimer.current);
    suppressTimer.current = window.setTimeout(() => {
      suppressClick.current = false;
    }, 420);
  };

  const enterCase = (force = false) => {
    if (!force && suppressClick.current) return;
    suppressClick.current = false;
    markVisited(study.id);
    if (discoveryQueue.length > 1) {
      setDiscoveryQueue(advanceDiscoveryQueue(discoveryQueue, study.id));
    }
    navigate(`/atlas/${study.id}`);
  };

  const goNext = (markAsSkipped = true) => {
    if (discoveryQueue.length < 2) return;
    clearLongPress();
    if (markAsSkipped) recordSkipped(study.id);
    setHistory((current) => [...current, study.id].slice(-12));
    const nextQueue = advanceDiscoveryQueue(discoveryQueue, study.id);
    setDiscoveryQueue(nextQueue);
    setLastHomeCase(nextQueue[0] ?? '');
    setMotion('next');
    setOffset(0);
    suppressForAWhile();
  };

  const goPrevious = () => {
    if (history.length === 0) return;
    clearLongPress();
    const previous = history[history.length - 1];
    setHistory((current) => current.slice(0, -1));
    setDiscoveryQueue([previous, ...discoveryQueue.filter((id) => id !== previous)]);
    setLastHomeCase(previous);
    setMotion('prev');
    setOffset(0);
    suppressForAWhile();
  };

  const openSheet = () => {
    clearLongPress();
    setOffset(0);
    setCopied(false);
    setSheetMode('actions');
    setSheetOpen(true);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    if (sheetOpen) return;
    clearLongPress();
    gesture.current = {
      x: event.clientX,
      y: event.clientY,
      pointerId: event.pointerId,
      longPress: false,
      dragging: true,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    timerRef.current = window.setTimeout(() => {
      gesture.current.longPress = true;
      openSheet();
      suppressForAWhile();
    }, 560);
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!gesture.current.dragging || gesture.current.pointerId !== event.pointerId) return;
    const dx = event.clientX - gesture.current.x;
    const dy = event.clientY - gesture.current.y;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) clearLongPress();
    if (gesture.current.longPress) return;
    if (Math.abs(dx) > Math.abs(dy) * 1.08) {
      event.preventDefault();
      setOffset(Math.max(-140, Math.min(140, dx)));
    }
  };

  const handlePointerEnd = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (gesture.current.pointerId !== event.pointerId) return;
    clearLongPress();
    const dx = event.clientX - gesture.current.x;
    const dy = event.clientY - gesture.current.y;
    gesture.current.dragging = false;
    if (gesture.current.longPress) {
      gesture.current.longPress = false;
      setOffset(0);
      return;
    }
    if (Math.abs(dx) > 54 && Math.abs(dx) > Math.abs(dy) * 1.15) {
      setOffset(0);
      suppressForAWhile();
      if (dx < 0) goNext(true);
      else goPrevious();
      return;
    }
    setOffset(0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goNext(true);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goPrevious();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      enterCase();
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}#/atlas/${study.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      window.prompt('复制这个案例链接：', url);
    }
  };

  return (
    <div
      className={`home-page ${motion === 'prev' ? 'is-backward' : ''}`}
      style={{ '--home-accent': study.accent, '--home-mark': study.id === 'mcdonalds' ? '#e0a62f' : study.accent } as CSSProperties}
    >
      <AmbientNetwork />

      <div className="home-topline">
        <span className="eyebrow">RANDOM ENTRY / 随机探索</span>
        <span className="home-position">
          {String(study.order).padStart(2, '0')} <i /> {String(cases.length).padStart(2, '0')}
        </span>
      </div>

      <div className="home-stage">
        <button className="home-arrow is-left" type="button" onClick={goPrevious} disabled={!hasPrevious} aria-label="上一个案例">
          <ChevronLeftIcon size={20} />
        </button>

        <div
          className={`home-case ${motion}`}
          key={study.id}
          style={{ transform: offset !== 0 ? `translateX(${offset}px) rotate(${offset * 0.008}deg)` : undefined }}
          role="button"
          tabIndex={0}
          aria-label={`${study.title}。点击进入案例网络；左右滑动切换案例；长按查看更多操作。`}
          onClick={() => enterCase()}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onContextMenu={(event) => event.preventDefault()}
        >
          <div className="home-mark-field">
            <div className="home-orbit orbit-one" />
            <div className="home-orbit orbit-two" />
            <div className="home-mark-glow" />
            <CaseMark study={study} className="home-mark" decorative />
          </div>

          <div className="home-case-copy">
            <div className="home-case-meta">
              <span>{category?.title}</span>
              <i />
              <span>{study.era}</span>
              {study.isNew ? <em>NEW / 新收录</em> : null}
            </div>
            <h1>{study.title}</h1>
            <p>
              {study.english}
              <span>·</span>
              {study.region}
            </p>
          </div>
        </div>

        <button className="home-arrow is-right" type="button" onClick={() => goNext(true)} disabled={!hasNext} aria-label="下一个案例">
          <ChevronRightIcon size={20} />
        </button>
      </div>

      <div className="home-bottom">
        <p className="home-hint">
          <span>点击进入</span>
          <i />
          <span>左滑换一个</span>
          <i />
          <span>长按操作</span>
        </p>
        <div className="home-state">
          <a className={viewed ? 'home-state-link is-current' : 'home-state-link'} href="#/viewed" aria-label="打开已看页面">
            <CheckIcon size={12} /> 已看 {viewedCases.length}
          </a>
          <a className={bookmarked ? 'home-state-link is-current' : 'home-state-link'} href="#/notebook" aria-label="打开我的收藏">
            <BookmarkIcon size={12} filled={bookmarked} /> 收藏 {bookmarkedCases.length}
          </a>
        </div>
      </div>

      {sheetOpen ? (
        <div className="home-sheet-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setSheetOpen(false)}>
          <section className="home-sheet" role="dialog" aria-modal="true" aria-labelledby="home-sheet-title">
            <div className="home-sheet-handle" />
            <header className="home-sheet-head">
              <div className="home-sheet-case">
                <CaseMark study={study} className="home-sheet-mark" decorative />
                <div>
                  <span className="eyebrow">{sheetMode === 'brief' ? 'CASE BRIEF' : 'HOLD ACTIONS'}</span>
                  <h2 id="home-sheet-title">{study.title}</h2>
                  <p>{study.english}</p>
                </div>
              </div>
              <button className="icon-button" type="button" onClick={() => setSheetOpen(false)} aria-label="关闭操作面板">
                <CloseIcon size={18} />
              </button>
            </header>

            {sheetMode === 'actions' ? (
              <div className="home-sheet-actions">
                <button
                  type="button"
                  className="home-action"
                  onClick={() => toggleCaseBookmark(study.id)}
                >
                  <BookmarkIcon size={18} filled={bookmarked} />
                  <span>
                    <strong>{bookmarked ? '取消收藏' : '收藏案例'}</strong>
                    <small>稍后从「我的」继续看</small>
                  </span>
                </button>
                <button
                  type="button"
                  className="home-action"
                  onClick={() => markCaseViewed(study.id)}
                  disabled={viewed}
                >
                  <CheckIcon size={18} />
                  <span>
                    <strong>{viewed ? '已经看过' : '标记为已看'}</strong>
                    <small>只有你主动标记，才计入「看过」</small>
                  </span>
                </button>
                <button type="button" className="home-action" onClick={() => setSheetMode('brief')}>
                  <InfoIcon size={18} />
                  <span>
                    <strong>案例简介与来源</strong>
                    <small>先看事实底盘和可回查来源</small>
                  </span>
                </button>
                <button type="button" className="home-action" onClick={() => void copyLink()}>
                  <LinkIcon size={18} />
                  <span>
                    <strong>{copied ? '链接已复制' : '复制入口链接'}</strong>
                    <small>分享这一个案例，而不是整站</small>
                  </span>
                </button>
                <button
                  type="button"
                  className="home-action is-skip"
                  onClick={() => {
                    setSheetOpen(false);
                    goNext(true);
                  }}
                >
                  <ArrowRightIcon size={18} />
                  <span>
                    <strong>跳过这个案例</strong>
                    <small>不会标记为已看，稍后再随机出现</small>
                  </span>
                </button>
              </div>
            ) : (
              <div className="home-brief">
                <p className="home-brief-lead">{study.summary}</p>
                <div className="home-brief-facts">
                  {study.facts.map((fact) => (
                    <p key={fact.text}>{fact.text}</p>
                  ))}
                </div>
                <div className="home-brief-sources">
                  <SourceList ids={study.sourceIds} compact />
                </div>
                <div className="home-brief-actions">
                  <button className="button button-quiet" type="button" onClick={() => setSheetMode('actions')}>
                    返回操作
                  </button>
                  <button
                    className="button button-primary"
                    type="button"
                    onClick={() => {
                      setSheetOpen(false);
                      enterCase(true);
                    }}
                  >
                    进入案例网络 <ArrowRightIcon size={15} />
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
